import React from 'react';
import classNames from 'classnames';
import { BASE_IMG_URL } from '@images/imageUrls';
import FeedbackProblemProductPickers, {
    type FeedbackProblemProductPickersHandle,
} from '@components/feedbackProblemProductPickers';
import type { FeedbackProductCategory, FeedbackProductItem, FetchProductsParams } from '@components/feedbackProductPicker';
import type { FeedbackProblemCategory, FeedbackProblemItem } from '@components/feedbackProblemPicker';
import type { FeedbackFilterOption, FeedbackFilterParams } from './index';

/** PC 端筛选区：筛选卡片 + 搜索栏 + 结果数量 */
export interface FeedbackFilterContentProps {
    filterParams: FeedbackFilterParams;
    searchInputValue: string;
    solveStateList: FeedbackFilterOption[];
    problemList: FeedbackFilterOption[];
    problemCategories: FeedbackProblemCategory[];
    productList: FeedbackFilterOption[];
    productCategories: FeedbackProductCategory[];
    daysList: FeedbackFilterOption[];
    totalCount?: number;
    layoutStyles?: Record<string, string>;
    onSearchChange?: (value: string) => void;
    onSearchConfirm?: () => void;
    onFilterChange?: (key: keyof FeedbackFilterParams, value: string) => void;
    onProblemSelect?: (problem: FeedbackProblemItem | null) => void;
    onProductSelect?: (product: FeedbackProductItem | null) => void;
    fetchProducts?: (params: FetchProductsParams) => Promise<FeedbackProductItem[]>;
}

interface FeedbackFilterContentState {}

class FeedbackFilterContent extends React.Component<FeedbackFilterContentProps, FeedbackFilterContentState> {
    pickerRef = React.createRef<FeedbackProblemProductPickersHandle>();

    handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            this.props.onSearchConfirm?.();
        }
    };

    handleSearchClear = () => {
        const { onSearchChange, onFilterChange, filterParams } = this.props;
        onSearchChange?.('');
        if (filterParams.searchValue) {
            onFilterChange?.('searchValue', '');
        }
    };

    getSelectedOption = (list: FeedbackFilterOption[], value: string) => {
        if (!value) return null;
        return list.find((item) => item.value === value) || null;
    };

    renderTagRow = (
        label: string,
        key: 'problemId' | 'productId',
        options: FeedbackFilterOption[],
        value: string,
        onPick?: () => void,
    ) => {
        const styles = this.props.layoutStyles || {};
        const selected = this.getSelectedOption(options, value);

        return (
            <div className={styles.filterRow}>
                <span className={styles.rowLabel}>{label}</span>
                <div className={styles.rowContent}>
                    <span
                        className={classNames(styles.allBtn, !value && styles.allBtnActive)}
                        onClick={() => {
                            this.props.onFilterChange?.(key, '');
                        }}
                    >
                        全部
                    </span>
                    {selected ? (
                        <span className={styles.selectedTag}>
                            <span className={styles.selectedTagText} onClick={() => onPick?.()}>{selected.name}</span>
                            <span
                                className={styles.tagClose}
                                onClick={() => this.props.onFilterChange?.(key, '')}
                            ></span>
                        </span>
                    ) : (
                        <span
                            className={styles.pickBtn}
                            onClick={() => onPick?.()}
                        >
                            选择{key == 'problemId' ? '类型' : '机型'}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    renderPillRow = (
        label: string,
        key: 'solveState' | 'daysValue',
        options: FeedbackFilterOption[],
        value: string,
    ) => {
        const styles = this.props.layoutStyles || {};

        return (
            <div className={styles.filterRow}>
                <span className={styles.rowLabel}>{label}</span>
                <div className={styles.rowContent}>
                    {options.map((item) => (
                        <span
                            key={`${key}-${item.id}`}
                            className={classNames(styles.pillBtn, value === item.value && styles.pillBtnActive)}
                            onClick={() => this.props.onFilterChange?.(key, item.value)}
                        >
                            {item.name}
                        </span>
                    ))}
                </div>
            </div>
        );
    };

    render() {
        const {
            filterParams,
            searchInputValue,
            solveStateList,
            problemList,
            problemCategories,
            productList,
            productCategories,
            daysList,
            totalCount = 0,
            layoutStyles = {},
            onSearchChange,
            onFilterChange,
            onProblemSelect,
            onProductSelect,
            fetchProducts,
        } = this.props;

        return (
            <>
                <div className={layoutStyles.filterCard}>
                    {this.renderTagRow(
                        '类型',
                        'problemId',
                        problemList,
                        filterParams.problemId,
                        () => this.pickerRef.current?.openProblemPicker(),
                    )}
                    {this.renderTagRow(
                        '机型',
                        'productId',
                        productList,
                        filterParams.productId,
                        () => this.pickerRef.current?.openProductPicker(),
                    )}
                    {this.renderPillRow('状态', 'solveState', solveStateList, filterParams.solveState)}
                    {this.renderPillRow('时间', 'daysValue', daysList, filterParams.daysValue)}
                </div>

                <div className={layoutStyles.searchBar}>
                    <div className={layoutStyles.searchBox}>
                        <img
                            src={`${BASE_IMG_URL}/common/search.png`}
                            alt="search"
                            className={layoutStyles.searchIcon}
                        />
                        <input
                            type="text"
                            className={layoutStyles.searchInput}
                            placeholder="搜索反馈问题"
                            value={searchInputValue}
                            onChange={(e) => onSearchChange?.(e.target.value)}
                            onKeyDown={this.handleSearchKeyDown}
                        />
                        {(!!searchInputValue || !!filterParams.searchValue) && (
                            <button
                                type="button"
                                className={layoutStyles.clearBtn}
                                onClick={this.handleSearchClear}
                                aria-label="清空"
                            />
                        )}
                    </div>
                    {/* <div className={layoutStyles.resultCount}>
                        相关问题反馈{totalCount}篇
                    </div> */}
                </div>

                <FeedbackProblemProductPickers
                    ref={this.pickerRef}
                    problemCategories={problemCategories}
                    productCategories={productCategories}
                    problemId={filterParams.problemId}
                    productId={filterParams.productId}
                    fetchProducts={fetchProducts}
                    onProblemSelect={onProblemSelect}
                    onProductSelect={onProductSelect}
                />
            </>
        );
    }
}

export default FeedbackFilterContent;
